import { useState, type ReactNode } from 'react';
import { STAGE_LABEL, userColorIndex, type KitNote, type NoteKind } from '@/domain';
import { formatInstant } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { LockIcon } from '@/ui/icons';
import styles from './CaseNotes.module.css';

const KIND_LABEL: Record<NoteKind, string> = {
  note: strings.notes.kindNote,
  rework: strings.notes.kindRework,
  flag: strings.notes.kindFlag,
  hold: strings.notes.kindHold,
  deletion: strings.notes.kindDeletion,
  redo: strings.notes.kindRedo,
  done: strings.notes.kindDone,
  assign: strings.notes.kindAssign,
  move: strings.notes.kindMove,
  resolved: strings.notes.kindResolved,
  passed: strings.notes.kindPassed,
  proof: strings.notes.kindProof,
  shipped: strings.notes.kindShipped,
  received: strings.notes.kindReceived,
};

/**
 * The notes on a case, on every screen that shows one. Section 3 of the request
 * of 16 September 2026.
 *
 * Notes about a case were written in five places and stored in two shapes: a
 * free note in its own row, a rework or flag reason on the history entry it
 * closed, a hold reason on the order, a deletion or redo reason in their own
 * blocks. Scattered by where they were kept rather than gathered by what they
 * mean, and none of them said who the note was for.
 *
 * `GET /kits/{kitId}/notes` gathers them; this draws them. Each card answers the
 * client's four questions in the order they asked: what kind of note it is,
 * where it came from and who it is for, what it says, and who wrote it when.
 *
 * With a `limit`, the newest that many and the rest behind a button that says
 * how many. Request of 24 September 2026: the job view is read on a phone, and
 * a case with a long conversation on it pushed its own details off the screen.
 * Newest first is the server's order, so the top of the list is the note the
 * next bench most needs.
 *
 * `extra` is what the screen has to show with a note and this card cannot
 * fetch for itself: the photographs of a proof row, which open in the image
 * viewer. The request of 5 October 2026 (third), section 6.
 */
export function CaseNotes({
  notes,
  limit,
  extra,
}: {
  notes: readonly KitNote[];
  limit?: number;
  /** Drawn under the words of a note; nothing for a note it has nothing for. */
  extra?: (note: KitNote) => ReactNode;
}) {
  // Whether the reader asked for the rest. Forgotten when the screen is left:
  // the next case's conversation is a different conversation.
  const [showingAll, setShowingAll] = useState(false);

  if (notes.length === 0) {
    return <p className={styles.empty}>{strings.notes.none}</p>;
  }

  const folds = limit !== undefined && notes.length > limit;
  const shown = folds && !showingAll ? notes.slice(0, limit) : notes;

  return (
    <div className={styles.notes}>
      {shown.map((note) => (
        <article key={note.id} className={`${styles.card} ${styles[note.kind] ?? ''}`}>
          <div className={styles.top}>
            <span className={styles.kind}>{KIND_LABEL[note.kind]}</span>
            <span className={styles.who}>
              {/* A person's name in that person's colour, as in Lab Notes. The
                  request of 5 October 2026 (third), section 4. */}
              <span
                className={styles.name}
                data-user-color={
                  note.byUserId === null || note.byUserName === ''
                    ? undefined
                    : userColorIndex(note.byUserId)
                }
              >
                {note.byUserName === '' ? strings.notes.bySystem : note.byUserName}
              </span>{' '}
              · {formatInstant(note.createdAt)}
            </span>
          </div>

          {/* Only when there is something to say: a free note is from the bench
              that wrote it and for whoever reads the case next. */}
          {note.toStage !== null && (
            <div className={styles.route}>
              {note.fromStage === null
                ? `${strings.notes.to} ${STAGE_LABEL[note.toStage]}`
                : `${strings.notes.from} ${STAGE_LABEL[note.fromStage]} ${strings.notes.to} ${STAGE_LABEL[note.toStage]}`}
            </div>
          )}
          {note.toStage === null && note.fromStage !== null && note.kind !== 'note' && (
            <div className={styles.route}>
              {strings.notes.from} {STAGE_LABEL[note.fromStage]}
            </div>
          )}

          {/* What the system recorded, "Assigned to Saad", above the reason or note with it. */}
          {note.event !== null && (
            <div className={styles.event}>
              {note.undoneAt != null ? <s>{note.event}</s> : note.event}
              {/* Taken back: the entry stays and says so. Section 6 of the request of 5 October 2026. */}
              {note.undoneAt != null && (
                <b>
                  {' · '}
                  {note.undoneByName != null && note.undoneByName !== ''
                    ? strings.labNotes.undoneBy(note.undoneByName)
                    : strings.labNotes.undone}
                </b>
              )}
            </div>
          )}
          {note.visibleTo !== null && (
            <div className={styles.lock}>
              <LockIcon size={12} />
              {strings.labNotes.onlyVisibleTo(note.visibleTo.name)}
            </div>
          )}

          {note.text !== '' && <div className={styles.text}>{note.text}</div>}
          {extra?.(note)}
        </article>
      ))}

      {folds && (
        <button
          type="button"
          className={styles.more}
          aria-expanded={showingAll}
          onClick={() => setShowingAll((current) => !current)}
        >
          {showingAll
            ? strings.notes.showFewer
            : `${strings.notes.showAll} ${notes.length} ${strings.notes.plural}`}
        </button>
      )}
    </div>
  );
}
